import { Button, Group, ScrollArea } from '@mantine/core'
import { IconGripVertical, IconPlus } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import classes from '../../styles/StudioEditorV2.module.css'

/**
 * The Figma slide switcher: one joined pill group with a drag grip per slide, and a
 * raised "Add Slide" button sitting outside the group.
 *
 * The grip is a drag affordance for reordering; keyboard users get the same reordering
 * through the arrow controls that stay available via `onMove`.
 */
export function SlideList({
  slides,
  selected,
  onSelect,
  onAdd,
  onMove,
  maxItems,
}) {
  const { t } = useTranslation()
  const move = (from, to) => {
    if (to < 0 || to >= slides.length || from === to) return
    onMove(from, to - from)
  }

  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <ScrollArea type="auto" style={{ flex: '0 1 auto', minWidth: 0 }}>
        <div className={classes.slideTabs} role="tablist" aria-label={t('editorV2.form.slides.slides')}>
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={index === selected}
              draggable
              className={index === selected ? classes.slideTabActive : classes.slideTab}
              onClick={() => onSelect(index)}
              onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(index))
                event.dataTransfer.effectAllowed = 'move'
              }}
              onDragOver={(event) => {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
              }}
              onDrop={(event) => {
                event.preventDefault()
                const from = Number(event.dataTransfer.getData('text/plain'))
                if (Number.isInteger(from)) move(from, index)
              }}
            >
              <IconGripVertical size={15} className={classes.slideTabGrip} aria-hidden />
              {t('editorV2.form.slides.slideNumber', { number: index + 1 })}
            </button>
          ))}
        </div>
      </ScrollArea>

      <Button
        variant="default"
        size="sm"
        radius="md"
        className={classes.addSlideButton}
        leftSection={<IconPlus size={16} />}
        disabled={slides.length >= maxItems}
        onClick={onAdd}
      >
        {t('editorV2.form.slides.addSlide')}
      </Button>
    </Group>
  )
}
